import type { ApprovalRequestStatus } from '../../approvals/approvalTypes';
import type { PurchaseRequestStatus } from './purchaseRequestTypes';

/**
 * The purchase-order wire types.
 *
 * **THE ENUMS ARE NUMERIC, because that is what the API serializes.** No
 * `JsonStringEnumConverter` is configured, so a request body carrying `"status": "Draft"` is
 * refused outright — the defect the purchase-request types shipped with, found by a 400 naming
 * `$.urgency`. The convention is `ISSUANCE_TYPE`'s: an `as const` object of numbers, and the
 * LABEL comes from the server on the DTO rather than from a second map here.
 *
 * The FACET values are the exception and are strings — the server emits enum NAMES there and
 * parses them with `Enum.TryParse`.
 */

/**
 * Where an order is in its own lifecycle.
 *
 * **The numbers are the PURCHASE REQUEST's, not the source's.** Two registers in one nav group
 * read the same way, so a reader moving between them meets one vocabulary. The source's own enum
 * conflates approval and dispatch in a single `Sent`.
 *
 * **5 AND 6 ARE DECLARED, and this sentence used to say they were not.** It read "two members
 * are RESERVED server-side and deliberately absent… there is no goods receipt in this product",
 * which was true until 30 Sep 2026 and false from the moment Goods Receipts landed — holding the
 * numbers is exactly what made adding them additive, and then they were added. The block comment
 * on the members themselves records what that cost when this map was a day behind the backend.
 */
export const PO_STATUS = {
  draft: 0,
  submitted: 1,
  approved: 2,
  rejected: 3,
  cancelled: 4,

  /*
    5 AND 6 WERE MISSING FROM THIS MAP FOR A DAY, and the backend declared them on 30 Sep 2026.
    A member the wire can send and this const does not name is one every comparison here answers
    `false` for — so a received order fell through every branch of the profile's banner and was
    described by whatever the default arm happened to say. The enum is NUMERIC on the wire, so
    nothing about that is a type error: `tsc` is clean either way, which is why the gap survived.

    THE RULE: a member declared on the backend is mirrored here in the SAME change.
  */
  partiallyReceived: 5,
  fullyReceived: 6,

  /*
    SENT — product call, 01 Oct 2026. An approval commits the organization; it does not say the
    paper left the building, and until somebody records that it did, nothing can be received
    against the order. See `PurchaseOrderStatuses.CanMarkSent` for why there is no way back.
  */
  sent: 7,
} as const;

export type PurchaseOrderStatus = (typeof PO_STATUS)[keyof typeof PO_STATUS];

export interface PurchaseOrderListItem {
  id: string;
  orderNumber: string;
  supplierId: string;
  supplierName: string;
  /**
   * The distinct purchase requests this order draws on, joined.
   *
   * **Resolved live from the lines.** There is no `purchaseRequestId` on an order — one order
   * may be composed of several requests, so a single column would state a fact the record
   * contradicts.
   */
  sourceRequestNumbers: string | null;
  /** How many distinct requests. Above one is what makes a row a composite. */
  sourceRequestCount: number;
  /** The distinct products the lines name, joined — concatenated into the anchor. */
  items: string | null;
  status: PurchaseOrderStatus;
  statusLabel: string;
  lineCount: number;
  totalQuantity: number;
  /**
   * What a goods receipt has ACCEPTED against the live lines — the numerator of the ring.
   *
   * **It moves at INSPECTION, not at registration** — 05 Oct 2026. The server reads it off
   * `GoodsReceiptItems.QuantityAccepted` on receipts at `Inspected` or `Received`, because a
   * receipt can be fully inspected with its stock already created and sit unclosed against its
   * order. Reading `PurchaseOrderItem.ReceivedQuantity` instead drew 0% on exactly those orders,
   * which is what the reader reported.
   *
   * **It is a QUANTITY, where the purchase request's ring counts LINES.** Product call,
   * 05 Oct 2026. The two registers watch different things: a requester asks how many of the
   * things they asked for have arrived, and an order's own question is how much of what it
   * committed to has been delivered — which a line count answers badly on an order whose
   * lines are part-delivered.
   *
   * **The stated cost is that it mixes units across lines** — fifty boxes and two laptops
   * added together is the figure `UnitOfMeasure` is a catalog to prevent. It is tolerable
   * here and nowhere else because the ring is a PROPORTION: both halves mix the same units in
   * the same proportion, so the fraction is honest even though neither figure is a quantity of
   * anything. **Do not promote either half to a column of its own**, where it would not be.
   */
  receivedQuantity: number;
  /** Never null: an order line carries a required unit cost, so a zero is a real figure. */
  totalAmount: number;
  expectedDeliveryDate: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

/**
 * How far through DELIVERY an order is — or null, where that is not a question yet.
 *
 * **ONE derivation, read by both views**, exactly as the purchase request's own is: the list
 * row draws it as a ring and the table states the same percentage in its `Fulfilled` column.
 * Written twice, the two would answer one order differently the first time either changed, and
 * each screen would look correct.
 *
 * **IT COUNTS QUANTITY, where the request's counts LINES** — product call, 05 Oct 2026. An
 * order's own question is how much of what it committed to has been delivered, and a line count
 * answers that badly on an order whose lines are part-delivered: nine of ten laptops on a
 * single line is 0% by lines and 90% by quantity. See `PurchaseOrderListItem.receivedQuantity`
 * for the cost that unit carries and why it is confined to this fraction.
 *
 * **THAT UNIT IS THE REFERENCE'S OWN**, which reopening its card is what established: it reads
 * *"Delivery fulfillment — received units ÷ ordered units across every PO line"*. The call
 * confirmed the source rather than departing from it. What this does NOT take is the rest of
 * that card — it reduces over the lines CLIENT-SIDE (this list DTO carries none), prints two
 * decimals, and calls the mixed sum "units received".
 *
 * **NULL IS NOT 0%**, and that distinction is the whole of what this function decides. A draft,
 * one awaiting a decision, one rejected, and one approved but still on the buyer's desk are not
 * *nothing delivered* — they are outside the question, and a ring on them would put an empty
 * circle down most of the register claiming nothing is happening to records where nothing can.
 *
 * **The gate is DISPATCH, which is the goods receipt's own gate read here.**
 * `PurchaseOrderStatuses.IsDispatched` is what `GoodsReceiptService` refuses on, so these are
 * exactly the statuses that can hold a non-zero received quantity — the gate is not withholding
 * a figure, it is declining to draw a ring on records where the figure is zero by construction.
 *
 * **A CANCELLED order keeps its ring where something arrived**, which is the one clause that is
 * not `IsDispatched`. Cancelling is legal from any status, so an order half delivered and then
 * withdrawn is a real state — and `Cancelled` overwrites the dispatch the status would
 * otherwise carry, so the received quantity is the only thing left that remembers it went out.
 * Goods that physically arrived stay counted, which is the call the request's ring already
 * takes one register along.
 */
export function fulfilmentOf(
  row: PurchaseOrderListItem,
): { done: number; total: number } | null {
  const DISPATCHED: PurchaseOrderStatus[] = [
    PO_STATUS.sent,
    PO_STATUS.partiallyReceived,
    PO_STATUS.fullyReceived,
  ];

  const inPipeline = DISPATCHED.includes(row.status) || row.receivedQuantity > 0;

  if (!inPipeline || row.totalQuantity <= 0) return null;

  return { done: row.receivedQuantity, total: row.totalQuantity };
}

export interface PurchaseOrderLine {
  id: string;
  /** The request line this fulfils — what carries the source request. */
  purchaseRequestItemId: string;
  purchaseRequestId: string;
  purchaseRequestNumber: string;
  inventoryItemId: string;
  sku: string;
  /** The catalog type's governed name — `Laptop`. */
  itemName: string;
  /** The product's own identity — `ACER Aspire 2`. Falls back to the type's name. */
  itemIdentity: string;
  unit: string;
  orderedQuantity: number;
  /** What the source request asked for on this line, across every order. */
  requestedQuantity: number;
  unitCost: number;
  lineTotal: number;
  /**
   * What a goods receipt has ACCEPTED against this line, capped at `orderedQuantity`.
   *
   * **`null` is not `0`.** Null says delivery is not yet a question — the order has not been
   * dispatched and nothing has arrived against any of its lines — where zero says it went out
   * and this line is still outstanding. The profile renders the first as an em dash and the
   * second as a muted `0%`, which is the same null-versus-zero distinction the register's own
   * `Fulfilled` column draws for the order as a whole.
   */
  deliveredQuantity: number | null;
  isActive: boolean;
}

/**
 * One ITEM's total across the order, however many lines name it.
 *
 * **Product call, 29 Sep 2026: two lines, one per source request, with a merged total.** Two
 * requests each asking for a laptop, both routed to one supplier, produce two lines — each
 * keeping its trace back to the request that asked for it — and this is what says the order is
 * for fifty-six laptops rather than leaving a reader to add 44 and 12 in their head.
 *
 * **The profile draws the merged row only where `lineCount` is above one.** An item on one line
 * has a merged total identical to that line's, so printing it would be the same value twice.
 */
export interface PurchaseOrderItemRollup {
  inventoryItemId: string;
  sku: string;
  itemIdentity: string;
  unit: string;
  lineCount: number;
  orderedQuantity: number;
  lineTotal: number;
}

/** One purchase request an order draws on, with how much of the order came from it. */
export interface PurchaseOrderSourceRequest {
  purchaseRequestId: string;
  requestNumber: string;
  lineCount: number;
  lineTotal: number;
}

export interface PurchaseOrderDetail {
  id: string;
  orderNumber: string;
  supplierId: string;
  supplierName: string;
  supplierCode: string;
  status: PurchaseOrderStatus;
  statusLabel: string;
  /** A Draft, and nothing else. Derived server-side. */
  isEditable: boolean;
  /**
   * Whether the document may still be marked as gone out to the supplier.
   *
   * **Derived server-side and SENT**, from the one predicate the endpoint refuses on — so the
   * banner and the refusal cannot disagree about whether this order is waiting to be dispatched.
   */
  canMarkSent: boolean;
  /**
   * Whether it has gone out, so a goods receipt may be registered against it.
   *
   * The other half of the pair, and the two are never both true: an order is either waiting to
   * be sent or already out.
   */
  isDispatched: boolean;

  /**
   * When it was recorded as gone out, and who recorded it.
   *
   * **Product call, 02 Oct 2026** — *when tagging PO as sent, add sent by/date*. Both are
   * stamped server-side by `MarkSentAsync`; no request carries a field for either, so a caller
   * cannot name the day a document went out.
   *
   * **NULL ON EVERY ORDER NOT YET SENT, and on the ones sent before this landed** — they were
   * never asked, and a backfill would have answered on their behalf. So every reader tests them
   * `== null`, never `===`: the API omits a null property entirely and it arrives as
   * `undefined`.
   */
  sentAt: string | null;

  /** Their display name as it was at that instant — a snapshot, like `createdByName`. */
  sentByName: string | null;

  expectedDeliveryDate: string | null;
  paymentTerms: string | null;
  notes: string | null;
  cancellationReason: string | null;
  items: PurchaseOrderLine[];
  /** Derived from the lines, server-side. An order's sources are whatever its lines fulfil. */
  sourceRequests: PurchaseOrderSourceRequest[];
  itemRollup: PurchaseOrderItemRollup[];
  totalAmount: number;
  totalQuantity: number;
  approvalStatus: ApprovalRequestStatus | null;
  /** That decision in words. The enum crosses the wire as a NUMBER, so nothing renders it raw. */
  approvalStatusLabel: string | null;
  approvalRequestId: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface PurchaseOrderListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  stage?: string;
  supplier?: string;
}

/** One request line an order may draw on, with how much of it is left. */
export interface OrderableRequestLine {
  purchaseRequestItemId: string;
  inventoryItemId: string;
  sku: string;
  itemName: string;
  itemIdentity: string;
  unit: string;
  requestedQuantity: number;
  orderedQuantity: number;
  /** Requested minus already ordered. The SERVER's figure — the form caps a quantity at it. */
  remainingQuantity: number;
  /** The requester's estimate. It SEEDS the order's unit cost and is not the same field. */
  estimatedUnitCost: number | null;
}

/**
 * One purchase request an order may be composed from.
 *
 * **The feed is a LIST and the form picks SEVERAL.** That is the one change the product owner
 * asked for: the source's own step 1 is a single-select card list, and here the same cards carry
 * a checkbox each and their lines pool together on the step after.
 */
export interface OrderablePurchaseRequest {
  id: string;
  requestNumber: string;
  /*
    THE STATUS IS BACK AND THE URGENCY IS NOT — 02 Oct 2026, and the half that returns does so
    with a consumer. This read "NO URGENCY AND NO STATUS. The feed offers only an approved
    request with something left to order, so a status here is true of every card and tells a
    reader nothing", which was exact on 01 Oct and expired the moment the feed widened: a
    PENDING request is in this list now, and the status is the ONE thing separating a card the
    reader may tick from a card they may only look at.

    THE URGENCY STAYS GONE. Nothing asks for it, and it is still a fact about the request rather
    than about what is left to order on it.
  */

  /** Where the request has got to — `submitted`, `approved` or `partiallyComplete`. */
  status: PurchaseRequestStatus;

  /** The reader's word for it — "For approval", "Approved", "Partially ordered". */
  statusLabel: string;

  /**
   * Whether an order may actually draw on this request.
   *
   * **READ, NEVER DERIVED FROM `status`.** The server answers it from
   * `PurchaseRequestUrgencies.IsOrderable`, which is the same rule `ResolveLinesAsync` refuses
   * on — so the card's own affordance and the endpoint's refusal cannot disagree. A
   * `status === PR_STATUS.approved || status === PR_STATUS.partiallyComplete` here would be
   * that comparison written twice, and the copy nobody is looking at is the one that drifts the
   * day a sixth status becomes orderable.
   *
   * **False means the card is DRAWN and cannot be ticked** — not that the row is left out. A
   * reader composing an order wants to see that the thing they are waiting on is in the queue,
   * which is the whole of the 02 Oct 2026 product call.
   */
  isOrderable: boolean;

  deliverToName: string | null;
  categories: string | null;
  items: string | null;
  /** The value of what is still ORDERABLE, not the whole request's estimate. */
  remainingEstimatedTotal: number | null;
  /**
   * Every live line on the request, covered or not — which `lines` CANNOT answer, because the
   * feed drops a line that is already covered. Counting that collection would report a
   * half-ordered request as untouched.
   */
  totalLineCount: number;
  /** How many of those are fully covered. `total - ordered` is exactly `lines.length`. */
  orderedLineCount: number;
  createdAt: string;
  createdByName: string | null;
  lines: OrderableRequestLine[];
}

/**
 * One line as the form sends it.
 *
 * **No id.** The set is addressed as a whole and reconciled server-side, so a line has nothing
 * for the client to address it by.
 */
export interface PurchaseOrderLineInput {
  purchaseRequestItemId: string;
  orderedQuantity: number;
  unitCost: number;
}

/**
 * Place or correct ONE order.
 *
 * **The wizard calls it once per supplier group.** Each supplier becomes its own order with its
 * own approval, so a partial failure is reportable per order rather than leaving one request
 * half-written — which is what an endpoint taking the whole split could not do.
 */
export interface PurchaseOrderRequest {
  supplierId: string;
  /** A calendar date (`YYYY-MM-DD`), never an instant — a delivery is promised for a day. */
  expectedDeliveryDate: string | null;
  paymentTerms: string | null;
  notes: string | null;
  items: PurchaseOrderLineInput[];
  /** Says what the reader intends to do NEXT. Never sent on an update. */
  submitImmediately?: boolean;
}
